import { Injectable } from '@angular/core';
import {
    LocalStorageNotificationUpdateService,
    NOTIFICATION_STATUS_CODE,
    NotificationModel,
    NotificationService,
} from '@evorait/pace-core';
import moment from 'moment';

/**
 * The statusCode of badge.
 * Will be translated in the frontend using this value as part of the translation key.
 * This value should fit to the values in the language bundle files.
 */
export enum STATUS_BADGE {
    NEW = 'NEW',
    OVERDUE = 'OVERDUE',
    IN_PROGRESS = 'IN_PROGRESS',
    RELEASED = 'RELEASED',
    CANCELLED = 'CANCELLED',
    TO_BE_SIGNED = 'TOBESIGNED',
    PAUSED = 'PAUSED',
}

export class BadgeInfo {
    public constructor(
        public readonly badge: STATUS_BADGE,
        public readonly ignoreOverdue: boolean,
    ) {}
}

@Injectable({
    providedIn: 'root',
})
export class StatusBadgeService {
    private STATUS_BADGE_MATRIX: { [key: string]: BadgeInfo } = {};
    public constructor(
        private localStorageNotificationUpdateService: LocalStorageNotificationUpdateService,
        private notificationService: NotificationService,
    ) {
        this.STATUS_BADGE_MATRIX[NOTIFICATION_STATUS_CODE.DISP] = new BadgeInfo(STATUS_BADGE.NEW, true);
        this.STATUS_BADGE_MATRIX[NOTIFICATION_STATUS_CODE.ETA] = new BadgeInfo(STATUS_BADGE.RELEASED, false);
        this.STATUS_BADGE_MATRIX[NOTIFICATION_STATUS_CODE.ROSR] = new BadgeInfo(STATUS_BADGE.IN_PROGRESS, true);
        this.STATUS_BADGE_MATRIX[NOTIFICATION_STATUS_CODE.FCC] = new BadgeInfo(STATUS_BADGE.IN_PROGRESS, true);
        this.STATUS_BADGE_MATRIX[NOTIFICATION_STATUS_CODE.TRAV] = new BadgeInfo(STATUS_BADGE.IN_PROGRESS, true);
        this.STATUS_BADGE_MATRIX[NOTIFICATION_STATUS_CODE.OSR] = new BadgeInfo(STATUS_BADGE.IN_PROGRESS, true);
        this.STATUS_BADGE_MATRIX[NOTIFICATION_STATUS_CODE.ATS] = new BadgeInfo(STATUS_BADGE.IN_PROGRESS, true);
        this.STATUS_BADGE_MATRIX[NOTIFICATION_STATUS_CODE.SOSR] = new BadgeInfo(STATUS_BADGE.IN_PROGRESS, true); // SOSR: Special OSR for PACE US
        this.STATUS_BADGE_MATRIX[NOTIFICATION_STATUS_CODE.AVR1] = new BadgeInfo(STATUS_BADGE.CANCELLED, true);
        this.STATUS_BADGE_MATRIX[NOTIFICATION_STATUS_CODE.AVR2] = new BadgeInfo(STATUS_BADGE.CANCELLED, true);
        this.STATUS_BADGE_MATRIX[NOTIFICATION_STATUS_CODE.CANC] = new BadgeInfo(STATUS_BADGE.CANCELLED, true);
        this.STATUS_BADGE_MATRIX[NOTIFICATION_STATUS_CODE.TOBS] = new BadgeInfo(STATUS_BADGE.TO_BE_SIGNED, true);
        this.STATUS_BADGE_MATRIX[NOTIFICATION_STATUS_CODE.RTOBS] = new BadgeInfo(STATUS_BADGE.TO_BE_SIGNED, true);
        this.STATUS_BADGE_MATRIX[NOTIFICATION_STATUS_CODE.FTOBS] = new BadgeInfo(STATUS_BADGE.TO_BE_SIGNED, true);
    }

    /**
     * Returns the translations key part based on the statusCode that is passed into it and based on the timeStamp.
     * If timeStamp indicates an overdue notification this is most likely returned. Otherwise its tried to find a
     * respective value based on the STATUS_BADGE_MATRIX.
     * It first tries to use statusCode and timestamp information from a locally stored statusCode. If the is not existing
     * it uses the information sent by the backend. This implies that an already accepted notification with ETA, which
     * was set to a different time than PlanStartTime, that this information is lost if the local storage information
     * is lost (due to new installation).
     *
     * @param notification the notification object
     * @returns the status badge object
     */
    public getBadgeStatus(notification: NotificationModel): BadgeInfo {
        // CANC overwrites everything
        const newUpdate = this.localStorageNotificationUpdateService.getLatestNewUpdate(notification.identifier);
        const status = this.notificationService.getStatus(notification);
        if (newUpdate && newUpdate.messageType === NOTIFICATION_STATUS_CODE.CANC) {
            status.statusCode = newUpdate.messageType;
            // This patch is needed if there is no What's New, due to re-installation of the app.
        } else if (
            notification.isCancelled &&
            (status.statusCode === NOTIFICATION_STATUS_CODE.DISP || status.statusCode === NOTIFICATION_STATUS_CODE.ETA)
        ) {
            status.statusCode = <NOTIFICATION_STATUS_CODE>notification.isCancelled;
        }

        const statusBadgeObject: BadgeInfo = this.STATUS_BADGE_MATRIX[status.statusCode];

        // statusBadgeObject is undefined for notifications in state CRM
        if (
            statusBadgeObject &&
            !statusBadgeObject.ignoreOverdue &&
            this.notificationService.getNotificationStart(notification).isBefore(moment())
        ) {
            return new BadgeInfo(STATUS_BADGE.OVERDUE, false);
        }

        if (notification.isPaused) {
            return new BadgeInfo(STATUS_BADGE.PAUSED, false);
        }

        return statusBadgeObject;
    }
}
